// Função para verificar se está em ambiente de produção
const isProduction = () => {
  const isProd = window.location.hostname !== 'localhost' && 
         window.location.hostname !== '127.0.0.1';
  console.log('isProduction:', isProd, 'hostname:', window.location.hostname);
  return isProd;
};

// Função para verificar se o Google Analytics está disponível
const isGtagAvailable = () => {
  const isAvailable = typeof window.gtag === 'function';
  console.log('isGtagAvailable:', isAvailable);
  return isAvailable;
};

// Função para verificar se o Google Analytics está funcionando corretamente
export const checkGA = () => {
  console.log('Checking Google Analytics setup...');
  
  // Verificar se estamos em produção
  if (!isProduction()) {
    console.log('Not in production mode, skipping GA check');
    return false;
  }
  
  // Verificar se o gtag está disponível
  if (!window.gtag) {
    console.error('Google Analytics not available (gtag not found)');
    return false;
  }
  
  // Verificar se o dataLayer está disponível
  if (!window.dataLayer) {
    console.error('DataLayer not available');
    return false;
  }
  
  // Verificar se o GA está configurado corretamente
  const configEvents = window.dataLayer.filter(event => 
    event[0] === 'config' && event[1] === 'G-HYB7VB9MPK'
  );
  
  if (configEvents.length === 0) {
    console.error('Google Analytics not configured correctly');
    return false;
  }
  
  console.log('Google Analytics is configured correctly');
  return true;
};

// Função para inicializar o Google Analytics
export const initGA = (measurementId) => {
  console.log('Initializing Google Analytics...');
  
  // Verificar se estamos em produção
  if (!isProduction()) {
    console.log('Not in production mode, skipping GA initialization');
    return;
  }
  
  // Verificar se o gtag já está disponível
  if (window.gtag) {
    console.log('Google Analytics already initialized');
    return;
  }
  
  try {
    // Criar o script do Google Analytics
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);
    
    // Inicializar o dataLayer
    window.dataLayer = window.dataLayer || [];
    window.gtag = function() {
      window.dataLayer.push(arguments);
    };
    
    // Configurar o Google Analytics
    window.gtag('js', new Date());
    window.gtag('config', measurementId, {
      send_page_view: true,
      debug_mode: true
    });
    
    console.log('Google Analytics initialized successfully');
  } catch (error) {
    console.error('Error initializing Google Analytics:', error);
  }
};

// Função para rastrear visualização de página
export const pageview = (url) => {
  if (!isProduction() || !isGtagAvailable()) return;
  
  window.gtag('config', 'G-HYB7VB9MPK', {
    page_path: url,
  });
};

// Função para rastrear seleção de opções
export const trackOptionSelection = (category, option, isSelected) => {
  if (!isProduction() || !isGtagAvailable()) return;
  
  console.log('Tracking option selection:', category, option, isSelected);
  
  // Formato GA4
  window.gtag('event', 'option_selection', {
    category: category,
    option: option,
    is_selected: isSelected ? 1 : 0
  });
};

// Função para rastrear geração de startup
export const trackStartupGeneration = (selectedSectors, selectedCharacteristics) => {
  if (!isProduction() || !isGtagAvailable()) return;
  
  console.log('Tracking startup generation:', selectedSectors, selectedCharacteristics);
  
  // Formato GA4
  window.gtag('event', 'startup_generation', {
    sectors: selectedSectors.join(', '),
    characteristics: selectedCharacteristics.join(', ')
  });
};

// Função para rastrear visualização do popup
export const trackPopupView = (startupName) => {
  if (!isProduction() || !isGtagAvailable()) return;
  
  console.log('Tracking popup view:', startupName);
  
  // Formato GA4
  window.gtag('event', 'popup_view', {
    startup_name: startupName
  });
};

// Função para rastrear cliques no botão CTA
export const trackCTAClick = (startupName) => {
  if (!isProduction() || !isGtagAvailable()) return;
  
  console.log('Tracking CTA click:', startupName);
  
  // Formato GA4
  window.gtag('event', 'cta_click', {
    startup_name: startupName
  });
};

// Função para rastrear fechamento do popup
export const trackPopupClose = (startupName) => {
  if (!isProduction() || !isGtagAvailable()) return;
  
  console.log('Tracking popup close:', startupName);
  
  // Formato GA4
  window.gtag('event', 'popup_close', {
    startup_name: startupName
  });
};

// Função para rastrear erros
export const trackError = (errorType, errorMessage) => {
  if (!isProduction() || !isGtagAvailable()) return;
  
  console.log('Tracking error:', errorType, errorMessage);
  
  // Formato GA4
  window.gtag('event', 'error', {
    error_type: errorType,
    error_message: errorMessage
  });
};

// Função para enviar um evento de teste
export const sendTestEvent = () => {
  console.log('Sending test event to Google Analytics...');
  
  // Verificar se estamos em produção
  if (!isProduction()) {
    console.log('Not in production mode, skipping test event');
    return;
  }
  
  // Verificar se o gtag está disponível
  if (!window.gtag) {
    console.error('Google Analytics not available (gtag not found)');
    return;
  }
  
  // Enviar evento de teste usando o método recomendado para GA4
  try {
    // Método 1: Usando o formato recomendado para GA4
    window.gtag('event', 'test_event', {
      event_category: 'debug',
      event_label: 'Test event from AI Startup Generator',
      value: 1,
      timestamp: new Date().toISOString()
    });
    
    // Método 2: Usando o formato alternativo
    window.gtag('event', 'test_event_alt', {
      category: 'debug',
      label: 'Alternative test event',
      value: 1
    });
    
    // Método 3: Usando o formato mais simples
    window.gtag('event', 'test_event_simple');
    
    console.log('Test events sent successfully');
    
    // Verificar se os eventos foram enviados
    setTimeout(() => {
      console.log('Checking if events were sent...');
      checkEvents();
    }, 1000);
  } catch (error) {
    console.error('Error sending test event:', error);
  }
};

// Função para verificar se os eventos estão sendo enviados corretamente
export const checkEvents = () => {
  console.log('Checking Google Analytics events...');
  
  // Verificar se estamos em produção
  if (!isProduction()) {
    console.log('Not in production mode, skipping event check');
    return;
  }
  
  // Verificar se o gtag está disponível
  if (!window.gtag) {
    console.error('Google Analytics not available (gtag not found)');
    return;
  }
  
  try {
    // Enviar um evento de verificação
    window.gtag('event', 'check_events', {
      event_category: 'debug',
      event_label: 'Event check from AI Startup Generator',
      value: 1,
      timestamp: new Date().toISOString()
    });
    
    // Verificar se o dataLayer está disponível
    if (window.dataLayer) {
      console.log('DataLayer is available');
      console.log('Recent events in dataLayer:', window.dataLayer.slice(-10));
    } else {
      console.log('DataLayer is not available');
    }
    
    // Verificar se o gtag está funcionando
    console.log('gtag function is available:', typeof window.gtag === 'function');
    
    // Verificar se o GA está configurado corretamente
    console.log('Google Analytics Measurement ID:', 'G-HYB7VB9MPK');
    
    console.log('Event check completed. Check the Google Analytics dashboard for events.');
  } catch (error) {
    console.error('Error checking events:', error);
  }
}; 